import React from 'react';
import { Elements } from 'prismic-reactjs';
import Router from 'next/router';
import linkResolver from './linkResolver';
import hrefResolver from './hrefResolver';
// Handler that will do routing imperatively on internal links
const onClickHandler = (href, as) => (e) => {
  e.preventDefault();
  Router.push(href, as);
};
const propsWithUniqueKey = (props, key) => Object.assign(props || {}, { key });

const htmlSerializer = function (type, element, content, children, key) {
  var props = {};
  switch (type) {
    case Elements.heading1: // Heading 1
      return React.createElement('h1', propsWithUniqueKey(props, key), children);

    case Elements.heading2: // Heading 2
      return React.createElement('h2', propsWithUniqueKey(props, key), children);

    case Elements.heading3: // Heading 3
      return React.createElement('h3', propsWithUniqueKey(props, key), children);

    case Elements.heading4: // Heading 4
      return React.createElement('h4', propsWithUniqueKey(props, key), children);

    case Elements.heading5: // Heading 5
      return React.createElement('h5', propsWithUniqueKey(props, key), children);

    case Elements.heading6: // Heading 6
      return React.createElement('h6', propsWithUniqueKey(props, key), children);

    case Elements.paragraph: // Paragraph
      return React.createElement('p', propsWithUniqueKey(props, key), children);

    case Elements.preformatted: // Preformatted
      return React.createElement('pre', propsWithUniqueKey(props, key), children);

    case Elements.strong: // Strong
      return React.createElement('strong', propsWithUniqueKey(props, key), children);

    case Elements.em: // Emphasis
      return React.createElement('em', propsWithUniqueKey(props, key), children);

    case Elements.listItem: // Unordered List Item
      return React.createElement('li', propsWithUniqueKey(props, key), children);

    case Elements.oListItem: // Ordered List Item
      return React.createElement('li', propsWithUniqueKey(props, key), children);

    case Elements.list: // Unordered List
      return React.createElement('ul', propsWithUniqueKey(props, key), children);

    case Elements.oList: // Ordered List
      return React.createElement('ol', propsWithUniqueKey(props, key), children);

    case Elements.embed: // Embed
      props = {
        'data-oembed': element.oembed.embed_url,
        'data-oembed-type': element.oembed.type,
        'data-oembed-provider': element.oembed.provider_name,
        ...(element.label ? { className: element.label } : {}),
      };
      const embedHtml = React.createElement('div', {
        dangerouslySetInnerHTML: {
          __html: element.oembed.html,
        },
        // ensure that embeded content is centered
        className: 'd-flex justify-content-center mb-3',
      });
      return React.createElement('div', propsWithUniqueKey(props, key), embedHtml);

    case Elements.hyperlink: // Link
      if (element.data.link_type === 'Document') {
        // Only for internal links add the new onClick that will imperatively route to the appropiate page
        props = {
          onClick: onClickHandler(hrefResolver(element.data), linkResolver(element.data)),
          href: linkResolver(element.data),
        };
        return React.createElement('a', propsWithUniqueKey(props, key), children);
      }
      // Default link handling
      const targetAttr = element.data.target ? { target: element.data.target } : {};
      const relAttr = element.data.target ? { rel: 'noopener' } : {};
      props = { href: element.data.url || linkResolver(element.data), ...targetAttr, ...relAttr };
      return React.createElement('a', propsWithUniqueKey(props, key), children);

    case Elements.image: // Image
      var props = {};
      var internal = false;

      if (element.linkTo && element.linkTo.link_type === 'Document') {
        // Exclusively for internal links, build the object that can be used for router push
        internal = true;
        props = {
          onClick: onClickHandler(hrefResolver(element.linkTo), linkResolver(element.linkTo)),
          href: linkResolver(element.linkTo),
        };
      }
      // Handle images just like regular HTML Serializer
      const linkUrl = element.linkTo ? element.linkTo.url || linkResolver(element.linkTo) : null;
      const linkTarget = (element.linkTo && element.linkTo.target) ? { target: element.linkTo.target } : {};
      const linkRel = linkTarget.target ? { rel: 'noopener' } : {};
      const img = React.createElement('img', { src: element.url, alt: element.alt || '' });
      return React.createElement(
        'p',
        propsWithUniqueKey({ className: [element.label || '', 'block-img'].join(' ') }, key),
        linkUrl ? React.createElement('a',
          // if it's an internal link, replace the onClick
          internal ? propsWithUniqueKey(props, key) : ({
            href: linkUrl,
            ...linkTarget,
            ...linkRel,
          }), img) : img,
      );

    case Elements.label: // Label
      props = element.data ? ({ className: element.data.label }) : {};
      return React.createElement('span', propsWithUniqueKey(props, key), children);

    case Elements.span: // Span
      if (content) {
        return content.split('\n').reduce((acc, p) => {
          if (acc.length === 0) {
            return [p];
          }
          const brIndex = (acc.length + 1) / 2 - 1;
          const br = React.createElement('br', propsWithUniqueKey({}, brIndex));
          return acc.concat([br, p]);
        }, []);
      }
      return null;

    default:
      return null;
  }
};

export default htmlSerializer;
